/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { FaEye, FaEyeSlash, FaRegCopy } from "react-icons/fa";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue } from "recoil";
import { SettingsHelpVideosAtom } from "../../atoms/SettingsHelpVideos";
import { UserAtom } from "../../atoms/UserAtom";
import { HelpVideoNameMap, IHelpContexts } from "../../types/HelpVideosTypes";
import StyledButton from "../StyledButton";

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  height: fit-content;

  margin: 40px 0px;
  padding: 48px;

  border-radius: 4px;
  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  cursor: default;

  @media screen and (max-width: 1140px) {
    padding: 48px 24px;
  }

  @media screen and (max-width: 560px) {
    margin: 20px 0px;

    padding: 24px;
  }
`;

const MainTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 18px;
`;

const SecondaryTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 16px;

  display: flex;
`;

const MainText = styled.div`
  color: #707070;
  font-weight: 400;
  font-size: 15px;
`;

const TextWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  grid-gap: 8px;

  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 24px;
  margin-bottom: 24px;

  border-top: 1px solid #eaeaeabf;
  padding-top: 24px;
  margin-top: 24px;

  &:first-of-type {
    border-top: unset;
    padding-top: unset;
    margin-top: unset;
  }
`;

const FormContent = styled.div`
  width: 100%;
  height: fit-content;

  padding: 0 14px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const SpaceBetween = styled.div`
  width: 100%;
  min-height: 42px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 840px) {
    flex-direction: column;
    align-items: flex-start;
    grid-gap: 16px;
  }
`;

const Input = styled.input`
  height: 45px;
  border: 1px solid;
  border-color: #eceeef;
  border-radius: 5px;
  padding: 14px;
  resize: none;
  background-color: #ffffff;
  font-size: 16px;
  letter-spacing: 0.17px;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  > svg {
    color: #fb972e;
    margin-right: 8px;
  }
`;

const InputGrid = styled.div`
  width: fit-content;

  display: flex;
  align-items: center;
  justify-content: center;

  grid-gap: 8px;

  position: relative;

  .button-wrapper {
    display: flex;
    align-items: center;
    grid-gap: 8px;
  }

  .icon-button {
    min-width: 45px;
    max-width: 45px;
    min-height: 45px;
    max-height: 45px;

    display: flex;
    align-items: center;
    justify-content: center;

    background: #fff;

    border-radius: 5px;
    border: 1px solid #eceeef;

    cursor: pointer;

    transition: all 0.3s ease-in-out;

    &:hover {
      background: #fb972e;
      border-color: #fb972e;

      svg {
        color: #fff;
      }
    }

    svg {
      width: 16px;
      height: 16px;

      color: #fb972e;

      transition: color 0.3s ease-in-out;
    }
  }

  @media screen and (max-width: 560px) {
    width: 100%;

    flex-direction: column;

    input,
    button,
    span {
      width: 100%;
    }
  }
`;

export default function Security(): JSX.Element | null {
  const user = useRecoilValue(UserAtom);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [, setHelpVideo] = useRecoilState(SettingsHelpVideosAtom);

  useEffect(() => {
    setHelpVideo({
      helpTitle: HelpVideoNameMap[IHelpContexts.SettingsApiKey],
      helpContext: IHelpContexts.SettingsApiKey,
    });
  }, []);

  function onCopy(text: string) {
    navigator.clipboard.writeText(text);

    toast("Api key successfully copied to the clipboard!", {
      type: "success",
    });
  }

  if (!user) return null;
  return (
    <Container>
      <TextWrapper>
        <MainTitle>Api Key</MainTitle>
        <MainText>
          Here are all your data for integrating third-party software with
          BerserkerMail.
        </MainText>
      </TextWrapper>

      <FormContent>
        <SpaceBetween>
          <SecondaryTitle>Your private Secret Key</SecondaryTitle>

          <InputGrid>
            <Input
              autoComplete={Math.random().toString()}
              value={user.apiKey}
              readOnly
              type={showSecretKey ? "text" : "password"}
              style={{ paddingRight: 40 }}
              css={css`
                height: 40px;
              `}
            />

            <span className="button-wrapper">
              <StyledButton
                onClick={() => setShowSecretKey((s) => !s)}
                inverted
                css={css`
                  height: 40px;
                  width: 108px;
                  padding: 9px 19px;
                `}
              >
                <IconContainer>
                  {showSecretKey ? (
                    <>
                      <FaEyeSlash />
                      <span>Hide</span>
                    </>
                  ) : (
                    <>
                      <FaEye />
                      <span>Show</span>
                    </>
                  )}
                </IconContainer>
              </StyledButton>
              <StyledButton
                onClick={() => onCopy(user.apiKey)}
                inverted
                css={css`
                  height: 40px;
                  padding: 9px 19px;
                `}
              >
                <IconContainer>
                  <FaRegCopy />
                  <span>Copy</span>
                </IconContainer>
              </StyledButton>
            </span>
          </InputGrid>
        </SpaceBetween>
      </FormContent>
    </Container>
  );
}
